import { AlertTriangle, Check, Copy, Flag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useEffect, useState } from 'react';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@/components/ui';
import { cn } from '@/lib/cn';

import { useFlagTurn } from '../hooks';
import type { ChatMessage } from '../store/chatStore';
import { SourceBadge } from './SourceBadge';

interface MessageBubbleProps {
  message: ChatMessage;
  isInternal: boolean;
  isAnalyst: boolean;
  onClarificationChip?: (text: string) => void;
}

/**
 * Single user/assistant message. Spec S04 §"MessageBubble anatomy".
 *
 * Notable behaviour:
 *  - assistant pending message shows a three-dot animation
 *  - source badges always rendered if present; popover interactivity depends on role
 *  - banner "raw sources" surfaces when `fallback_used`
 *  - SPARQL block only shown for analysts (debug)
 *  - Flag button only shown for internal users on assistant messages
 *  - clarification chips render below the bubble
 */
export function MessageBubble({
  message,
  isInternal,
  isAnalyst,
  onClarificationChip,
}: MessageBubbleProps) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-lg bg-brand-50 px-4 py-2 text-body text-brand-900">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.pending) {
    return (
      <div className="flex max-w-[80%] items-center gap-1 rounded-lg bg-slate-100 px-4 py-3 text-slate-500">
        <Dot className="animate-pulse" />
        <Dot className="animate-pulse [animation-delay:120ms]" />
        <Dot className="animate-pulse [animation-delay:240ms]" />
      </div>
    );
  }

  if (message.error) {
    return (
      <div className="flex max-w-[80%] items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-700">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        {message.error}
      </div>
    );
  }

  return (
    <div className="flex max-w-[85%] flex-col gap-2">
      <div className="rounded-lg bg-slate-100 px-4 py-3 text-body text-slate-900">
        {message.fallbackUsed && (
          <div className="-mx-1 mb-2 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-small text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            <span>Raw sources — not validated in the knowledge graph.</span>
          </div>
        )}

        <div className="prose prose-sm max-w-none text-body">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>

        {message.sources && message.sources.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-0.5">
            {message.sources.map((s, i) => (
              <SourceBadge
                key={`${s.source_id}-${i}`}
                badge={s}
                interactive={isInternal}
                index={i}
              />
            ))}
          </div>
        )}

        {isAnalyst && message.sparqlGenerated && (
          <SparqlBlock
            sparql={message.sparqlGenerated}
            resultCount={message.resultCount}
          />
        )}
      </div>

      <div className="flex items-center gap-3 px-1">
        {message.confidence && <ConfidenceDot value={message.confidence} />}
        {isInternal && <FlagButton turnId={message.turnId} />}
      </div>

      {message.clarificationChips && message.clarificationChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {message.clarificationChips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => onClarificationChip?.(chip)}
              className="rounded-full border border-accent/40 bg-white px-3 py-1 text-small text-accent-700 hover:bg-accent/10"
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function SparqlBlock({
  sparql,
  resultCount,
}: {
  sparql: string;
  resultCount: number | null | undefined;
}) {
  const [copied, setCopied] = useState(false);

  // Auto-reset the "copied" confirmation after a short delay so the
  // button reverts to its normal affordance without an extra click.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = async (e: React.MouseEvent) => {
    // <details> would toggle if the click bubbled to its <summary>.
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(sparql);
      setCopied(true);
    } catch {
      // Older browsers / non-secure contexts — fall back to a textarea
      // selection so analysts still get something they can Ctrl-C.
      const ta = document.createElement('textarea');
      ta.value = sparql;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  return (
    <details className="mt-3 rounded border border-slate-200 bg-white p-2 text-small">
      <summary className="flex cursor-pointer items-center justify-between gap-2 font-mono text-slate-500">
        <span>SPARQL ({resultCount ?? 0} rows)</span>
        <button
          type="button"
          onClick={copy}
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-small transition-colors',
            copied
              ? 'bg-green-50 text-green-700'
              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
          )}
          aria-label={copied ? 'Copied' : 'Copy SPARQL to clipboard'}
          title={copied ? 'Copied' : 'Copy SPARQL to clipboard'}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" aria-hidden />
              Copy
            </>
          )}
        </button>
      </summary>
      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-small text-slate-700">
        {sparql}
      </pre>
    </details>
  );
}

function Dot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-1.5 w-1.5 rounded-full bg-slate-400', className)}
    />
  );
}

function ConfidenceDot({ value }: { value: 'high' | 'medium' | 'low' }) {
  const color =
    value === 'high'
      ? 'bg-green-500'
      : value === 'medium'
        ? 'bg-amber-500'
        : 'bg-red-500';
  return (
    <div className="flex items-center gap-1 text-small text-slate-500">
      <span aria-hidden className={cn('h-2 w-2 rounded-full', color)} />
      <span>{value}</span>
    </div>
  );
}

function FlagButton({ turnId }: { turnId: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const flag = useFlagTurn();

  const submit = () => {
    if (!reason.trim()) return;
    flag.mutate(
      { turnId, reason: reason.trim() },
      {
        onSuccess: () => {
          setOpen(false);
          setReason('');
        },
      },
    );
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Flag className="mr-1 h-3 w-3" aria-hidden />
        <span className="text-small">Flag</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag this response</DialogTitle>
            <DialogDescription>
              Describe what's wrong. Your feedback joins the Training Signal Store.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What went wrong?"
            rows={4}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!reason.trim() || flag.isPending}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Use a Badge for the message-level fallback indicator when needed.
export { Badge as _Badge };
